import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { euro, fmtDate, apiError, notifyChange, can } from "../lib/api";
import { useQuick } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PayBadge } from "../components/StatusBadge";
import { ArrowLeft, Pencil, Upload, FileText, Trash2, Download, Plus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const DOC_CATS = ["referto RX", "risonanza", "prescrizione", "certificato", "documento", "altro"];

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openModal } = useQuick();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [noteToAdmin, setNoteToAdmin] = useState(false);
  const isPhysio = !can(user, "pagamenti", "view");
  const [cat, setCat] = useState("referto RX");
  const [desc, setDesc] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/patients/${id}`);
      setData(data);
      api.get(`/patients/${id}/notes`).then(({ data: n }) => setNotes(n)).catch(() => setNotes([]));
    } catch { toast.error("Paziente non trovato"); navigate("/pazienti"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ll:data-changed", h);
    return () => window.removeEventListener("ll:data-changed", h);
  }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("patient_id", id);
      fd.append("category", cat);
      fd.append("description", desc);
      await api.post("/documents/upload", fd);
      toast.success("Documento caricato");
      setDesc("");
      load();
    } catch (err) { toast.error(apiError(err, "Errore upload")); } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/file/${doc.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Errore nel download"); }
  };

  const delDoc = async (doc) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    await api.delete(`/documents/${doc.id}`);
    toast.success("Documento eliminato");
    load();
  };

  if (!data) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" /></div>;
  const p = data.patient;

  const saveNote = async () => {
    if (!noteText.trim()) { toast.error("Scrivi il testo della nota"); return; }
    try {
      await api.post(`/patients/${id}/notes`, { text: noteText, to_admin: noteToAdmin });
      toast.success(noteToAdmin ? "Segnalazione inviata alla titolare" : "Nota aggiunta");
      setNoteText("");
      setNoteToAdmin(false);
      notifyChange();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="patient-detail-page">
      <div className="flex items-center gap-3">
        <button data-testid="patient-back" onClick={() => navigate("/pazienti")} className="p-2 rounded-xl bg-white border border-slate-200"><ArrowLeft size={16} /></button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold">{p.first_name} {p.last_name}</h1>
          <p className="text-sm text-slate-500">{p.phone || "—"} {p.email ? `· ${p.email}` : ""} {p.birth_date ? `· Nato il ${fmtDate(p.birth_date)}` : ""}</p>
        </div>
        <button data-testid="patient-edit" onClick={() => openModal("patient", { patient: p })} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium hover:border-blue-300"><Pencil size={14} /> Modifica</button>
        <button data-testid="patient-new-appointment" onClick={() => openModal("appointment")} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-medium"><Plus size={14} /> Appuntamento</button>
      </div>

      {(p.op_notes || p.admin_notes) && (
        <div className="grid md:grid-cols-2 gap-3">
          {p.op_notes && <div className="bg-cyan-50 border border-cyan-100 rounded-2xl p-4" data-testid="patient-op-notes"><p className="text-[10px] uppercase tracking-widest text-cyan-500 mb-1">Note operative</p><p className="text-sm text-cyan-900">{p.op_notes}</p></div>}
          {p.admin_notes && <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4" data-testid="patient-admin-notes"><p className="text-[10px] uppercase tracking-widest text-amber-500 mb-1">Note amministrative</p><p className="text-sm text-amber-900">{p.admin_notes}</p></div>}
        </div>
      )}

      {!isPhysio && (
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="patient-total-paid">
          <p className="text-xs uppercase tracking-widest text-slate-400">Totale pagato</p>
          <p className="font-heading text-2xl font-bold text-emerald-600 mt-1">{euro(data.total_paid)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="patient-total-due">
          <p className="text-xs uppercase tracking-widest text-slate-400">Da pagare</p>
          <p className="font-heading text-2xl font-bold text-red-500 mt-1">{euro(data.total_due)}</p>
        </div>
      </div>
      )}

      <Tabs defaultValue="appuntamenti" className="bg-white rounded-2xl border border-slate-100 p-5">
        <TabsList data-testid="patient-tabs">
          <TabsTrigger value="appuntamenti" data-testid="tab-appointments">Appuntamenti</TabsTrigger>
          <TabsTrigger value="note" data-testid="tab-notes">Note interne</TabsTrigger>
          {!isPhysio && <TabsTrigger value="pagamenti" data-testid="tab-payments">Pagamenti</TabsTrigger>}
          <TabsTrigger value="documenti" data-testid="tab-documents">Documenti</TabsTrigger>
          <TabsTrigger value="palestra" data-testid="tab-gym">Palestra</TabsTrigger>
        </TabsList>

        <TabsContent value="appuntamenti" className="mt-4">
          <table className="w-full text-sm" data-testid="patient-appointments-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-3">Data</th><th className="pr-3">Trattamento</th><th className="pr-3">Fisioterapista</th><th className="pr-3">Stato</th><th className="pr-3">Importo</th>
            </tr></thead>
            <tbody>
              {data.appointments.map((a) => (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer" onClick={() => openModal("appointment", { appointment: a })} data-testid={`patient-appt-${a.id}`}>
                  <td className="py-2.5 pr-3 whitespace-nowrap">{fmtDate(a.date)} <span className="text-slate-400">{a.start}</span></td>
                  <td className="pr-3">{a.treatment_name}</td>
                  <td className="pr-3"><span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: a.physio_color }} />{a.physio_name}</span></td>
                  <td className="pr-3"><StatusBadge status={a.status} /></td>
                  <td className="pr-3">{euro(a.price)} <PayBadge status={a.payment_status} /></td>
                </tr>
              ))}
              {data.appointments.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nessun appuntamento</td></tr>}
            </tbody>
          </table>
        </TabsContent>

        <TabsContent value="note" className="mt-4">
          <div className="bg-slate-50 rounded-xl p-3 mb-4 space-y-2" data-testid="note-form">
            <textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
              placeholder="Nota interna sul paziente (visibile al team)..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:border-blue-400" />
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox data-testid="note-to-admin" checked={noteToAdmin} onCheckedChange={(c) => setNoteToAdmin(Boolean(c))} />
                Invia come segnalazione ai destinatari configurati
              </label>
              <button data-testid="note-save" onClick={saveNote} className="ml-auto bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-medium hover:bg-blue-700 transition-colors">Aggiungi nota</button>
            </div>
          </div>
          <div className="space-y-2" data-testid="notes-list">
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-row-${n.id}`} className="border border-slate-100 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{n.author_name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">{n.author_role}</span>
                  {n.to_admin && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Segnalazione alla titolare</span>}
                  <span className="text-xs text-slate-400 ml-auto">{new Date(n.created_at).toLocaleString("it-IT")}</span>
                </div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{n.text}</p>
              </div>
            ))}
            {notes.length === 0 && <p className="text-center text-slate-400 text-sm py-8" data-testid="notes-empty">Nessuna nota interna</p>}
          </div>
        </TabsContent>

        {!isPhysio && (
        <TabsContent value="pagamenti" className="mt-4">
          <table className="w-full text-sm" data-testid="patient-payments-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-3">Data</th><th className="pr-3">Causale</th><th className="pr-3">Metodo</th><th className="pr-3">Importo</th>
            </tr></thead>
            <tbody>
              {data.payments.map((pay) => (
                <tr key={pay.id} className="border-b border-slate-50">
                  <td className="py-2.5 pr-3">{fmtDate(pay.date)}</td>
                  <td className="pr-3">{pay.causale || "—"}</td>
                  <td className="pr-3 capitalize">{pay.method}</td>
                  <td className="pr-3 font-semibold text-emerald-600">{euro(pay.amount)}</td>
                </tr>
              ))}
              {data.payments.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">Nessun pagamento registrato</td></tr>}
            </tbody>
          </table>
        </TabsContent>
        )}

        <TabsContent value="documenti" className="mt-4">
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-xl p-3 mb-4" data-testid="doc-upload-area">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="doc-category" className="w-40 rounded-xl bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <input data-testid="doc-description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Descrizione (opzionale)" className="flex-1 min-w-40 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white" />
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.webp" onChange={upload} className="hidden" data-testid="doc-file-input" />
            <button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50">
              <Upload size={14} /> {uploading ? "Caricamento..." : "Carica documento"}
            </button>
          </div>
          <div className="space-y-2" data-testid="patient-documents-list">
            {data.documents.map((d) => (
              <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 border border-slate-100 rounded-xl px-4 py-3 hover:bg-slate-50/50">
                <FileText size={18} className="text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{d.filename}</p>
                  <p className="text-xs text-slate-400">{d.category} · {new Date(d.created_at).toLocaleDateString("it-IT")} {d.description ? `· ${d.description}` : ""}</p>
                </div>
                <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Download size={16} /></button>
                <button data-testid={`doc-delete-${d.id}`} onClick={() => delDoc(d)} className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
              </div>
            ))}
            {data.documents.length === 0 && <p className="text-center text-slate-400 text-sm py-8">Nessun documento caricato</p>}
          </div>
        </TabsContent>

        <TabsContent value="palestra" className="mt-4">
          <table className="w-full text-sm" data-testid="patient-gym-table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-3">Data</th><th className="pr-3">Entrata</th><th className="pr-3">Uscita</th><th className="pr-3">Attività</th><th className="pr-3">Importo</th><th className="pr-3">Pagamento</th>
            </tr></thead>
            <tbody>
              {data.gym.map((g) => (
                <tr key={g.id} className="border-b border-slate-50">
                  <td className="py-2.5 pr-3">{fmtDate(g.date)}</td>
                  <td className="pr-3 font-mono">{g.checkin_time}</td>
                  <td className="pr-3 font-mono">{g.checkout_time || "—"}</td>
                  <td className="pr-3 text-slate-600">{g.activity || "—"}</td>
                  <td className="pr-3">{euro(g.amount)}</td>
                  <td className="pr-3">{g.paid ? <span className="text-emerald-600 text-xs font-medium">Pagato ({g.method})</span> : <span className="text-red-500 text-xs font-medium">Da pagare</span>}</td>
                </tr>
              ))}
              {data.gym.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Nessun ingresso in palestra</td></tr>}
            </tbody>
          </table>
        </TabsContent>
      </Tabs>
    </div>
  );
}
